import React from "react";
import { ArrowUpRight, MessageCircle, Mail, Phone, Linkedin,
         Twitter, Facebook, Instagram, Youtube, Send, Globe, MessagesSquare } from "lucide-react";
import { useSeoMetadata } from "@/hooks/useSeoMetadata";
import { trackChannelClick } from "@/lib/ga";

/**
 * ContactChannels — CMS-driven contact & social channel list.
 *
 * All channels are read from `/api/seo/site-metadata` which normalises
 * SiteSettings (WhatsApp, business email, phones, socialLinks) into a
 * single ordered array. Adding a new platform in Payload (Telegram,
 * WeChat, X, Signal, etc.) immediately appears here — no code change.
 *
 * Every rendered link fires `contact_channel_click` with the platform
 * slug so GA4 exploration can group by channel without a code deploy
 * for each new platform.
 */
export default function ContactChannels({ location = "contact-page", className = "" }) {
  const { data: seoMeta } = useSeoMetadata();
  const channels = seoMeta?.channels || [];
  if (channels.length === 0) return null;

  const featured = channels.find((c) => c.featured) || null;
  const rest = channels.filter((c) => c !== featured);

  return (
    <div className={className} data-testid="contact-channels">
      {featured && (
        <FeaturedChannelCard channel={featured} location={location} />
      )}
      {rest.length > 0 && (
        <ul className={`space-y-3 ${featured ? "mt-4" : ""}`}>
          {rest.map((c) => (
            <li key={`${c.platform}:${c.url}`}>
              <ChannelCard channel={c} location={location} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Featured card — used for the primary chat channel (WhatsApp when
 * present). Distinct dark treatment + "Fastest reply" badge. Applies
 * to WHICHEVER channel is marked `featured: true` by the backend
 * ordering — no platform-specific styling.
 */
function FeaturedChannelCard({ channel, location }) {
  return (
    <a
      href={channel.url}
      target={channel.external ? "_blank" : undefined}
      rel={channel.external ? "noopener noreferrer" : undefined}
      onClick={() => trackChannelClick(channel, location)}
      data-testid={`channel-featured-${channel.platform}`}
      className="group block rounded-[20px] p-6 md:p-7 text-white transition-all hover:-translate-y-0.5"
      style={{
        backgroundColor: "#0a0a0a", border: "1px solid #1a1a1a",
        boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 20px 48px -24px rgba(0,0,0,0.35)",
      }}
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-2 py-0.5 text-[10px] tracking-[0.16em] uppercase text-white/80">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Fastest reply
        </span>
      </div>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-white/60">
            {channel.label}
          </p>
          <p className="mt-2 text-[18px] font-medium text-white">
            {channel.display}
          </p>
        </div>
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 border border-white/15">
          <ChannelIcon platform={channel.platform} size={18} strokeWidth={1.5} />
        </span>
      </div>
      <span className="mt-6 inline-flex items-center gap-1 text-[13px] font-medium text-white group-hover:opacity-90">
        Message us on {channel.label} <ArrowUpRight size={14} />
      </span>
    </a>
  );
}

/**
 * Standard card — every non-featured channel gets one. Icon looked up
 * from ChannelIcon() which falls back to <Globe /> for platforms it
 * doesn't recognise. Recognising a new icon requires code, but *rendering*
 * a new platform does not — the fallback is always graceful.
 */
function ChannelCard({ channel, location }) {
  return (
    <a
      href={channel.url}
      target={channel.external ? "_blank" : undefined}
      rel={channel.external ? "noopener noreferrer" : undefined}
      onClick={() => trackChannelClick(channel, location)}
      data-testid={`channel-${channel.platform}`}
      className="mts-card group flex items-center gap-4 p-5"
    >
      <div className="mts-icon-chip">
        <ChannelIcon platform={channel.platform} size={18} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
          {channel.label}
        </p>
        <p className="mt-1 text-[14.5px] font-medium text-[#0a0a0a] truncate">
          {channel.display}
        </p>
      </div>
      <ArrowUpRight size={14} className="text-[#86868b] group-hover:text-[#0a0a0a]" />
    </a>
  );
}

/**
 * Icon lookup — best-effort, graceful fallback for unknown platforms so
 * a new channel added in CMS still renders (just with a generic globe).
 * Extending this map is optional and non-blocking: no icon → globe.
 */
const _ICONS = {
  whatsapp: MessageCircle,
  email: Mail,
  phone: Phone,
  linkedin: Linkedin,
  twitter: Twitter, x: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  telegram: Send,
  wechat: MessagesSquare,
};
function ChannelIcon({ platform, ...props }) {
  const C = _ICONS[String(platform || "").toLowerCase()] || Globe;
  return <C {...props} />;
}
